import { admin, admin2 } from '@/api/instance'
import { isBenefitSharingEnabled, resolveBenefitShopScope } from '@/use/useOrgBenefitControl'
/**
 * @template T
 * @typedef {Promise<[T, string|null]>} Api2Response
 */

const baseUrl = (shopId) => {
  return `/${shopId}/classTicket`
}

const record = (shopId) => {
  return `/${shopId}/classTicketRecord`
}
const useRecord = (shopId) => {
  return `/${shopId}/classTicketRecordUse`
}

/**
 * @typedef {Object} CourseBindings
 * @property {string} courseInfoId - 課程ID
 * @property {number} requiredQuantity - 堂票綁定課程所需數量
 */
/**
 * 建立堂票
 * admin.classTicket.create
 * @param {Object} params
 * @param {string} params.shopId - 店家ID
 * @param {string} params.name - 堂票名稱
 * @param {string} params.description - 堂票描述
 * @param {number} params.availableTimes - 可使用次數
 * @param {boolean} params.isExp - 是否有限制
 * @param {number} params.expDay - 有效天數
 * @param {string} params.expType - 有效期限類型
 * @param {string} params.endOfPeriodUnit - 有效期限單位
 * @param {string} params.specifyExpiredDate - 特定截止日期
 * @param {number} params.order - 排序
 * @param {string} params.notice - 注意事項
 * @param {boolean} params.isValuable - 是否可轉贈
 * @param {boolean} params.isClientUsageAllowed - 是否開放客人核銷
 * @param {string} params.image - 圖片
 * @param {Array<string>} params.appointmentServices - 預約服務
 * @param {Array<CourseBindings>} params.courseBindings - 綁定課程
 * @returns {Promise}
 */
export const CreateClassTicket = async ({
  shopId,
  name,
  description,
  availableTimes,
  isExp,
  expDay,
  expType,
  endOfPeriodUnit,
  specifyExpiredDate,
  order,
  notice,
  isValuable,
  isClientUsageAllowed,
  image,
  appointmentServices,
  courseBindings,
}) => {
  return await admin2({
    method: 'post',
    url: `${baseUrl(shopId)}`,
    data: {
      name,
      description,
      availableTimes,
      isExp,
      expDay,
      expType,
      endOfPeriodUnit,
      specifyExpiredDate,
      order,
      notice,
      isValuable,
      isClientUsageAllowed,
      image,
      appointmentServices,
      courseBindings,
    },
  })
}

/**
 * 取得堂票列表
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {number} [params.start=0] - 查詢起始
 * @param {number} [params.limit=100] - 查詢筆數
 * @param {string} [params.name] - 名稱搜尋
 * @param {boolean} [params.crossShop] - 是否查詢跨店共用資料（舊版相容）
 * @param {'local'|'all'|'shared'} [params.benefitShopScope] - 適用類型篩選，優先於 crossShop
 * @param {string} [params.issuingShopId] - 發行店ID
 * @param {string} [params.grantedShopId] - 發放店ID（舊版相容，會映射為 issuingShopId）
 * @returns {Promise<Array>} 堂票列表
 */
export const GetClassTicket = async ({
  shopId,
  start,
  limit,
  name,
  crossShop,
  benefitShopScope,
  issuingShopId,
  grantedShopId,
}) => {
  return await admin2({
    method: 'get',
    url: `${baseUrl(shopId)}`,
    params: {
      start,
      limit,
      name,
      benefitShopScope: resolveBenefitShopScope({
        benefitShopScope,
        crossShop,
        fallbackCrossShop: isBenefitSharingEnabled('classTicket', shopId) ? true : undefined,
      }),
      issuingShopId: issuingShopId ?? grantedShopId,
    },
  })
}

/**
 * 取得堂票總數
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} [params.name] - 名稱搜尋
 * @param {boolean} [params.crossShop] - 是否查詢跨店共用資料（舊版相容）
 * @param {'local'|'all'|'shared'} [params.benefitShopScope] - 適用類型篩選，優先於 crossShop
 * @param {string} [params.issuingShopId] - 發行店ID
 * @param {string} [params.grantedShopId] - 發放店ID（舊版相容，會映射為 issuingShopId）
 * @returns {Promise<Object>} 堂票總數
 */
export const GetClassTicketCount = async ({
  shopId,
  name,
  crossShop,
  benefitShopScope,
  issuingShopId,
  grantedShopId,
}) => {
  return await admin2({
    method: 'get',
    url: `${baseUrl(shopId)}/count`,
    params: {
      name,
      benefitShopScope: resolveBenefitShopScope({
        benefitShopScope,
        crossShop,
        fallbackCrossShop: isBenefitSharingEnabled('classTicket', shopId) ? true : undefined,
      }),
      issuingShopId: issuingShopId ?? grantedShopId,
    },
  })
}

//= > 查找堂票
export const FindClassTicket = async ({ shopId, id }) => {
  return await admin2({
    method: 'get',
    url: `${baseUrl(shopId)}/${id}`,
  })
}

//= > 更新堂票
export const UpdateClassTicket = async ({ shopId, id, name, description, image, notice, appointmentServices, order, courseBindings }) => {
  return await admin2({
    method: 'put',
    url: `${baseUrl(shopId)}/${id}`,
    data: {
      name,
      description,
      image,
      notice,
      appointmentServices,
      order,
      courseBindings,
    },
  })
}

//= > 刪除堂票
export const DeleteClassTicket = async ({ shopId, id }) => {
  return await admin2({
    method: 'delete',
    url: `${baseUrl(shopId)}/${id}`,
  })
}

// ! 紀錄

//= > 建立堂票紀錄
export const CreateClassTicketRecord = async ({
  shopId,
  memberId,
  classTicketId,
  quantity,
  price,
  note,
}) => {
  const res = await admin({
    method: 'post',
    url: `${record(shopId)}`,
    data: {
      memberId,
      classTicketId,
      quantity,
      price,
      note,
    },
  })

  return res.data
}

//= > 取得堂票紀錄
export const GetClassTicketRecord = async ({
  shopId,
  start,
  limit,
  code,
  MemberId,
  ClassTicketId,
  createdAtStart,
  createdAtEnd,
  benefitShopScope,
  crossShop,
  grantedShopId,
  redemptionShopId,
}) => {
  const res = await admin({
    method: 'get',
    url: `${record(shopId)}`,
    params: {
      start,
      limit,
      code,
      MemberId,
      ClassTicketId,
      createdAtStart,
      createdAtEnd,
      grantedShopId,
      redemptionShopId,
      benefitShopScope: resolveBenefitShopScope({
        benefitShopScope,
        crossShop,
        fallbackCrossShop: isBenefitSharingEnabled('classTicket', shopId) ? true : undefined,
      }),
    },
  })

  return res.data
}

export const GetClassTicketRecord2 = async ({
  shopId,
  start,
  limit,
  code,
  MemberId,
  ClassTicketId,
  benefitShopScope,
  crossShop,
}) => {
  return await admin2({
    method: 'get',
    url: `${record(shopId)}`,
    params: {
      start,
      limit,
      code,
      MemberId,
      ClassTicketId,
      benefitShopScope: resolveBenefitShopScope({
        benefitShopScope,
        crossShop,
        fallbackCrossShop: isBenefitSharingEnabled('classTicket', shopId) ? true : undefined,
      }),
    },
  })
}

//= > 取得堂票紀錄總數
export const GetClassTicketRecordCount = async ({
  shopId, code, MemberId, ClassTicketId, createdAtStart,
  createdAtEnd, benefitShopScope, crossShop, grantedShopId, redemptionShopId,
}) => {
  const res = await admin({
    method: 'get',
    url: `${record(shopId)}/count`,
    params: {
      code,
      MemberId,
      ClassTicketId,
      createdAtStart,
      createdAtEnd,
      grantedShopId,
      redemptionShopId,
      benefitShopScope: resolveBenefitShopScope({
        benefitShopScope,
        crossShop,
        fallbackCrossShop: isBenefitSharingEnabled('classTicket', shopId) ? true : undefined,
      }),
    },
  })

  return res.data.count
}

//= > 查找堂票紀錄
export const FindClassTicketRecord = async ({ shopId, id }) => {
  const res = await admin({
    method: 'get',
    url: `${record(shopId)}/${id}`,
  })

  return res.data
}

//= > 使用堂票
export const UseClassTicket = async ({ shopId, id, times }) => {
  const res = await admin({
    method: 'post',
    url: `${record(shopId)}/${id}/use`,
    data: {
      times,
    },
  })

  return res.data
}

//= > 使用堂票
export const CancelUseClassTicket = async ({ shopId, id, times }) => {
  const res = await admin({
    method: 'post',
    url: `${record(shopId)}/${id}/useCancel`,
    data: {
      times,
    },
  })

  return res.data
}

//= > 取消堂票紀錄
export const CancelClassTicketRecord = async ({ shopId, id }) => {
  const res = await admin({
    method: 'put',
    url: `${record(shopId)}/${id}/cancel`,
  })

  return res.data
}

// 查詢堂票序號詳細紀錄
export const GetClassTicketRecordSerialDetail = async ({
  shopId,
  id,
}) => {
  return await admin2({
    method: 'get',
    url: `${record(shopId)}/${id}/serialMore`,
  })
}

/**
 * 查詢會員堂票紀錄與課程所需數量
 * admin.classTicketRecord.findByCourseBind
 * @param {Object} param0
 * @param {string} param0.shopId - 店家 ID
 * @param {string} param0.memberId - 會員 ID
 * @param {string} param0.courseInfoId - 課程 ID
 * @returns {Api2Response<Array<{requiredQuantity: number | null, classTicketRecords: Array<Object>}>}
 */
export const GetClassTicketRecordByCourseBind = async ({ shopId, memberId, courseInfoId }) => {
  return await admin2({
    method: 'get',
    url: `${record(shopId)}/findByCourseBind`,
    params: {
      memberId,
      courseInfoId,
    },
  })
}

// 堂票使用紀錄
export const GetClassTicketUseRecord = async ({
  shopId,
  start,
  limit,
  code,
  MemberId,
  ClassTicketId,
  SerialActivityId,
  type,
  createdAtStart,
  createdAtEnd,
  crossShop,
  grantedShopId,
  redemptionShopId,
}) => {
  const res = await admin({
    method: 'get',
    url: `${useRecord(shopId)}`,
    params: {
      start,
      limit,
      code,
      MemberId,
      ClassTicketId,
      SerialActivityId,
      type,
      createdAtStart,
      createdAtEnd,
      grantedShopId,
      redemptionShopId,
      crossShop: crossShop ?? (isBenefitSharingEnabled('classTicket', shopId) ? true : undefined),
    },
  })

  return res.data
}

//= > 取得堂票紀錄總數
export const GetClassTicketUseRecordCount = async ({
  shopId, code, MemberId, ClassTicketId, SerialActivityId,
  type, createdAtStart,
  createdAtEnd,
  crossShop,
  grantedShopId,
  redemptionShopId,
}) => {
  const res = await admin({
    method: 'get',
    url: `${useRecord(shopId)}/count`,
    params: {
      code,
      MemberId,
      ClassTicketId,
      SerialActivityId,
      type,
      createdAtStart,
      createdAtEnd,
      grantedShopId,
      redemptionShopId,
      crossShop: crossShop ?? (isBenefitSharingEnabled('classTicket', shopId) ? true : undefined),
    },
  })

  return res.data.count
}

//= > 查詢堂票序號
export const GetClassTicketRecordSerial = async ({
  shopId,
  ClassTicketRecordId,
  createdAtStart,
  createdAtEnd,
  start,
  limit,
  MemberId,
  crossShop,
}, { oldInstance } = {}) => {
  let apiInstance = admin2
  if (oldInstance)apiInstance = admin
  const res = await apiInstance({
    method: 'get',
    url: `/${shopId}/classTicketRecordSerial`,
    params: {
      ClassTicketRecordId,
      createdAtStart,
      createdAtEnd,
      start,
      limit,
      MemberId,
      crossShop: crossShop ?? (isBenefitSharingEnabled('classTicket', shopId) ? true : undefined),
    },
  })

  if (oldInstance) return res.data
  return res
}
export const GetClassTicketRecordSerialOld = async ({
  shopId,
  ClassTicketRecordId,
  createdAtStart,
  createdAtEnd,
  start,
  limit,
  MemberId,
  crossShop,
}) => {
  const res = await admin({
    method: 'get',
    url: `/${shopId}/classTicketRecordSerial`,
    params: {
      ClassTicketRecordId,
      createdAtStart,
      createdAtEnd,
      start,
      limit,
      MemberId,
      crossShop: crossShop ?? (isBenefitSharingEnabled('classTicket', shopId) ? true : undefined),
    },
  })

  return res.data
}

//= > 查詢堂票序號數量
export const GetClassTicketRecordSerialCount = async ({
  shopId,
  ClassTicketRecordId,
  createdAtStart,
  createdAtEnd,
  MemberId,
  crossShop,
}, { oldInstance } = {}) => {
  let apiInstance = admin2
  if (oldInstance)apiInstance = admin
  const res = await apiInstance({
    method: 'get',
    url: `/${shopId}/classTicketRecordSerial/count`,
    params: {
      ClassTicketRecordId,
      createdAtStart,
      createdAtEnd,
      MemberId,
      crossShop: crossShop ?? (isBenefitSharingEnabled('classTicket', shopId) ? true : undefined),
    },
  })
  if (oldInstance) return res.data.count
  return res
}

//= > 查詢堂票序號
export const FindClassTicketRecordSerial = async ({
  shopId,
  id,
}) => {
  return await admin2({
    method: 'get',
    url: `/${shopId}/classTicketRecordSerial/${id}`,
  })
}
